import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { Upload, Trash2, Video as VideoIcon } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminEvents = () => {
  const [events, setEvents] = useState([]);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [uploading, setUploading] = useState(false);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/events`);
      setEvents(response.data);
    } catch (error) {
      toast.error('Failed to load events');
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error('Please select a video');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', title || 'Event');
    formData.append('date', date || new Date().toLocaleDateString());

    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${BACKEND_URL}/api/admin/events`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success('Video uploaded successfully!');
      setFile(null);
      setTitle('');
      setDate('');
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this video?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${BACKEND_URL}/api/admin/events/${eventId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      toast.success('Video deleted successfully');
      fetchEvents();
    } catch (error) {
      toast.error('Failed to delete video');
    }
  };

  return (
    <div>
      <h2 className="text-3xl font-black text-white mb-8 uppercase">Events Management</h2>

      {/* Upload Form */}
      <Card className="bg-zinc-900 border-zinc-800 mb-8">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Upload className="w-5 h-5 text-red-600" />
            Upload Event Video
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleUpload} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-white font-bold uppercase text-sm">Video File *</Label>
              <Input
                type="file"
                accept="video/*"
                onChange={(e) => setFile(e.target.files[0])}
                className="bg-zinc-800 border-zinc-700 text-white"
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white font-bold uppercase text-sm">Event Title *</Label>
                <Input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g., Midrand Fresher"
                  className="bg-zinc-800 border-zinc-700 text-white placeholder:text-gray-500"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label className="text-white font-bold uppercase text-sm">Event Date</Label>
                <Input
                  type="text"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  placeholder="e.g., March 2026"
                  className="bg-zinc-800 border-zinc-700 text-white placeholder:text-gray-500"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={uploading}
              className="w-full bg-red-600 hover:bg-red-700 text-white font-bold uppercase"
            >
              {uploading ? 'Uploading...' : 'Upload Video'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Events Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {events.map((event) => (
          <Card key={event.id} className="bg-zinc-900 border-zinc-800 group">
            <CardContent className="p-0">
              <div className="relative aspect-video overflow-hidden bg-zinc-800">
                <video
                  src={`${BACKEND_URL}${event.video_url}`}
                  controls
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h3 className="text-white font-bold text-lg">{event.title}</h3>
                    <p className="text-red-500 text-sm">{event.date}</p>
                    <p className="text-gray-500 text-xs mt-1">
                      {new Date(event.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <Button
                    onClick={() => handleDelete(event.id)}
                    variant="destructive"
                    size="sm"
                    className="bg-red-600 hover:bg-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {events.length === 0 && (
        <div className="text-center py-20">
          <VideoIcon className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400 text-lg">No videos uploaded yet</p>
        </div>
      )}
    </div>
  );
};

export default AdminEvents;
